/**
 * Helper murni FILTER UKURAN (faset "Ukuran" di `/cari`, mig 197). Label ukuran
 * datang dari RPC `search_public_products` (faset `sizes` — diekstrak dari opsi
 * varian grup ber-nama Ukuran/Size, sudah dinormalkan di DB oleh
 * `normalize_size_label`: XXL→2XL, "allsize"→All Size, angka tanpa nol depan).
 * Modul ini hanya MENGURUTKAN label supaya chip tampil masuk akal:
 *   1. ukuran huruf  : 2XS → XS → S → M → L → XL → 2XL → … → 9XL
 *   2. ukuran kata   : Small → Standar → Jumbo → Super Jumbo → Extra Jumbo → All Size
 *   3. ukuran angka  : menaik — ukuran anak (2–20) otomatis sebelum sepatu (21–41)
 *   4. lainnya       : alfabetis (label bebas toko tetap tampil)
 * Tanpa React/Supabase supaya teruji di Vitest (`sizes.test.ts`).
 */

const LETTER_ORDER = [
  "4XS", "3XS", "2XS", "XS", "S", "M", "L", "XL",
  "2XL", "3XL", "4XL", "5XL", "6XL", "7XL", "8XL", "9XL",
];

const WORD_ORDER = [
  "small",
  "standar",
  "jumbo",
  "super jumbo",
  "extra jumbo",
  "all size",
];

/** Indeks urut ukuran huruf; toleran bentuk XXL/XXXL (data lama tanpa normalisasi). */
function letterIndex(upper: string): number {
  const multi = /^(X{2,8})([SL])$/.exec(upper);
  const canon = multi ? `${multi[1].length}X${multi[2]}` : upper;
  return LETTER_ORDER.indexOf(canon);
}

export type SizeSortKey = {
  /** 0 = huruf · 1 = kata · 2 = angka · 3 = lainnya. */
  group: number;
  /** Urutan di dalam grup (indeks huruf/kata, nilai angka; lainnya 0). */
  rank: number;
  /** Pembanding akhir (lowercase) supaya urutan deterministik. */
  text: string;
};

/** Kunci urut sebuah label ukuran (lihat urutan grup di header modul). */
export function sizeSortKey(label: string): SizeSortKey {
  const trimmed = label.trim();
  const text = trimmed.toLowerCase();

  const letter = letterIndex(trimmed.toUpperCase());
  if (letter >= 0) return { group: 0, rank: letter, text };

  const word = WORD_ORDER.indexOf(text.replace(/[\s-]+/g, " "));
  if (word >= 0) return { group: 1, rank: word, text };

  if (/^\d{1,3}$/.test(trimmed)) {
    return { group: 2, rank: parseInt(trimmed, 10), text };
  }

  return { group: 3, rank: 0, text };
}

// ── Normalisasi label ukuran (cermin SQL `normalize_size_label`, mig 197) ────
//
// PARITAS WAJIB dengan `public.normalize_size_label(p_raw, p_strict)`. Daftar
// kasus yang sama hidup di `supabase/tests/promo_size_rules_3670.sql`; kalau
// salah satu berubah, ubah keduanya. Menyimpang berarti admin melihat daftar
// ukuran yang berbeda dari ukuran yang benar-benar bisa dihargai.
//
// `strict` menentukan sikap terhadap teks yang TIDAK dikenali:
//   • false → dikembalikan apa adanya ("label bebas toko tetap dipertahankan").
//     Dipakai SQL untuk opsi pada grup ber-nama Ukuran/Size, di mana konteksnya
//     sudah menjamin "ini memang ukuran".
//   • true  → null. Dipakai saat menebak ukuran dari TOKEN NAMA VARIAN, di mana
//     tak ada yang menjamin apa pun — "Merah" bukan ukuran.
// "Standar" sengaja null saat strict: itu nama varian bawaan produk TANPA
// variasi, dan menganggapnya ukuran akan menandai hampir seluruh katalog.

const LETTER_BASE = new Set(["XS", "S", "M", "L", "XL"]);

export function normalizeSizeLabel(
  raw: string | null | undefined,
  strict = false
): string | null {
  const t = (raw ?? "").trim();
  if (!t) return null;
  const u = t.toUpperCase();

  if (LETTER_BASE.has(u)) return u;
  // XXL → 2XL, XXXL → 3XL … (panjang − 1 huruf X sebelum S/L).
  const multiL = /^(X{2,8})L$/.exec(u);
  if (multiL) return `${multiL[1].length}XL`;
  const multiS = /^(X{2,8})S$/.exec(u);
  if (multiS) return `${multiS[1].length}XS`;
  if (/^[2-9]X[SL]$/.test(u)) return u;

  if (u === "SMALL") return "Small";
  if (u === "STANDAR" || u === "STANDARD" || u === "STANDART") {
    return strict ? null : "Standar";
  }
  if (u === "JUMBO") return "Jumbo";
  if (/^SUPER[ -]?JUMBO$/.test(u)) return "Super Jumbo";
  if (/^EXTRA[ -]?JUMBO$/.test(u)) return "Extra Jumbo";
  if (/^ALL[ -]?SIZE$/.test(u)) return "All Size";

  if (/^[0-9]{1,2}$/.test(u)) {
    const n = parseInt(u, 10);
    if (n >= 1 && n <= 60) return String(n);
  }

  return strict ? null : t;
}

/**
 * Ukuran sebuah VARIAN, ditebak dari namanya ("Merah / 2XL" → "2XL").
 *
 * SENGAJA memakai `strict` dan SENGAJA hanya bersandar pada `name`:
 *
 * • strict → satu-satunya arah gagalnya adalah "aturan ukuran tidak menyala",
 *   yaitu pembeli membayar potongan utama kampanye — harga yang tetap
 *   disengaja admin. Kebalikannya (menebak "Merah" sebagai ukuran) akan
 *   memasang potongan yang tak pernah dimaksudkan, pada kolom uang.
 * • hanya `name` → fungsi ini dipanggil dari dalam `getVariantPrice`, yang
 *   dipakai storefront, checkout, dan keranjang admin. Bila ukurannya harus
 *   di-precompute lalu ditempelkan ke `ProductVariation`, satu mapper yang lupa
 *   mengisinya akan membuat harga berbeda antar layar TANPA error — kelas bug
 *   yang paling mahal di repo ini. `name` selalu ada di semua jalur.
 *
 * Cermin cabang fallback `compute_product_sizes` (mig 197): pisah dengan "/",
 * normalisasi tiap token, ambil yang PERTAMA dikenali.
 */
export function variantSizeLabel(variantName: string | null | undefined): string | null {
  const raw = (variantName ?? "").trim();
  if (!raw) return null;
  for (const seg of raw.split("/")) {
    const label = normalizeSizeLabel(seg, true);
    if (label) return label;
  }
  return null;
}

/**
 * Bisakah label ini BENAR-BENAR dicocokkan ke sebuah varian?
 *
 * Ini pasangan jujur `variantSizeLabel`, dan ia ada untuk menutup satu bentuk
 * bug yang spesifik: daftar ukuran yang ditawarkan editor kampanye datang dari
 * `products.sizes`, yang dibangun dengan normalisasi NON-strict (opsi pada grup
 * ber-nama Ukuran/Size dipertahankan apa adanya, termasuk "Bayi"). Sementara
 * itu pencocok harga memakai normalisasi STRICT atas nama varian. Selisihnya
 * berarti admin bisa mengisi potongan untuk "Bayi", menyimpannya tanpa error,
 * lalu tak satu harga pun berubah — checkbox mati yang persis sebentuk dengan
 * kunci popup `checkout`.
 *
 * Editor memakai ini untuk MENANDAI label semacam itu sebelum disimpan, bukan
 * membuangnya: label bebas toko tetap sah tersimpan (dan akan berguna begitu
 * sumbu opsi varian—butir 2.2—dikerjakan), yang tak boleh adalah admin
 * mengiranya bekerja.
 */
export function isMatchableSizeLabel(label: string | null | undefined): boolean {
  const normalized = normalizeSizeLabel(label, false);
  if (!normalized) return false;
  return normalizeSizeLabel(normalized, true) === normalized;
}

/** Urutkan label ukuran utk baris chip (tak memutasi input). */
export function sortSizeLabels(labels: string[]): string[] {
  return [...labels].sort((a, b) => {
    const ka = sizeSortKey(a);
    const kb = sizeSortKey(b);
    if (ka.group !== kb.group) return ka.group - kb.group;
    if (ka.rank !== kb.rank) return ka.rank - kb.rank;
    return ka.text.localeCompare(kb.text) || a.localeCompare(b);
  });
}
